import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, Trash2, Loader2, ChevronDown, RotateCcw } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Label } from '@/components/shadcn/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { ColorPicker } from '@/components/common/ColorPicker'
import { cn } from '@/lib/utils'
import {ICON_BY_NAME, iconForName} from "@/components/common/iconByName.tsx";

const ICON_ENTRIES = Object.entries(ICON_BY_NAME)

/**
 * Symbol selection (shared: action modules + service modules). Two mutually
 * exclusive modes (as in the old portal): either an uploaded image
 * OR an icon + color. When an image is set, icon/color selection is hidden;
 * it becomes available again only after removing the image. iconName/colorHex are
 * preserved in the process (the backend only deletes the IconUrl), so the icon
 * comes back unchanged after removal.
 *
 * `fallbackIconName` is an optional key from ICON_BY_NAME that represents the
 * type-specific fallback (e.g. "url" for URL modules). It is never written back —
 * it is purely used for display when no iconName is set, and to decide whether
 * the reset button should be shown (reset is pointless when the active icon
 * already matches the fallback).
 */
export function IconColorPicker({
                                  iconName,
                                  colorHex,
                                  iconUrl,
                                  busy,
                                  uploadDisabled = false,
                                  fallbackIconName,
                                  onIconChange,
                                  onColorChange,
                                  onUpload,
                                  onDelete,
                                  disabled = false,
                                }: {
  iconName: string | null
  colorHex: string
  iconUrl: string | null
  busy: boolean
  /** Hide the image upload (e.g. on create, while the object has no Id yet). */
  uploadDisabled?: boolean
  /**
   * Key from ICON_BY_NAME shown when no iconName is set — never written back,
   * purely visual. Also used to suppress the reset button when the active icon
   * already matches the fallback.
   */
  fallbackIconName?: string | null
  onIconChange: (name: string) => void
  onColorChange: (hex: string) => void
  onUpload: (file: File, dataUrl: string) => void
  onDelete: () => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [iconPickerOpen, setIconPickerOpen] = useState(false)

  // Same normalization as iconForName(): stored values like "Folder" or
  // " folder" must still resolve to the correct icon.
  const normalizedIconName = iconName?.trim().toLowerCase() || null

  const PreviewIcon = iconForName(iconName, iconForName(fallbackIconName))

  // Show reset only when an icon is explicitly set AND it differs from the fallback —
  // resetting to the fallback when it's already showing would be a no-op visually.
  const showReset = !!normalizedIconName && normalizedIconName !== fallbackIconName

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = evt => {
      const dataUrl = evt.target?.result as string
      onUpload(file, dataUrl)
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('iconColorPicker.icon')}</Label>
      <div className="flex items-start gap-3">
        {/* Preview: uploaded image takes priority, then the resolved icon */}
        <div className="flex size-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
          {iconUrl ? (
            <img src={iconUrl} alt="" className="size-full object-cover" />
          ) : PreviewIcon ? (
            <PreviewIcon className="size-8" style={colorHex ? { color: colorHex } : undefined} />
          ) : null}
        </div>

        <input disabled={disabled} ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />

        {iconUrl ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={onDelete}
                disabled={busy || disabled}
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('iconColorPicker.removeImage')}</TooltipContent>
          </Tooltip>
        ) : uploadDisabled ? null : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy||disabled}
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('iconColorPicker.uploadImage')}</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Icon + color only selectable without an image */}
      {!iconUrl && (
        <>
          {/* Icon selection in the popover: the grid is only rendered on open, so
              color changes don't re-draw ~40 icons (lag on the color picker). */}
          {/* modal: otherwise the surrounding dialog's react-remove-scroll blocks
              the mouse wheel in the (portaled-out) popover content. */}
          <div className="flex items-center gap-2 mt-1">
            <Popover modal open={iconPickerOpen} onOpenChange={setIconPickerOpen}>
              <PopoverTrigger asChild>
                <Button disabled={disabled} variant="outline" size="sm" className="w-fit gap-2">
                  {PreviewIcon && (
                    <PreviewIcon className="size-4" style={colorHex ? { color: colorHex } : undefined} />
                  )}
                  {t('iconColorPicker.chooseIcon')}
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <div className="flex max-h-48 flex-wrap gap-1 overflow-y-auto scrollbar-custom">
                  {ICON_ENTRIES.map(([name, Icon]) => (
                    <button
                      key={name}
                      type="button"
                      title={name}
                      onClick={() => { onIconChange(name); setIconPickerOpen(false) }}
                      className={cn(
                        'flex size-8 items-center justify-center rounded-md border transition-colors',
                        normalizedIconName === name ? 'border-foreground bg-muted' : 'border-transparent hover:bg-muted',
                      )}
                      disabled={disabled}
                    >
                      <Icon className="size-4" style={{ color: colorHex }} />
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Reset button: only shown when an icon is explicitly set and differs
                from the type-specific fallback — resets to "no icon" so the
                fallback is displayed automatically. */}
            {showReset && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={disabled}
                    variant="ghost"
                    size="icon"
                    onClick={() => onIconChange('')}
                  >
                    <RotateCcw className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('iconColorPicker.resetIcon')}</TooltipContent>
              </Tooltip>
            )}
          </div>

          <ColorPicker disabled={disabled} value={colorHex} onChange={onColorChange} className="mt-1" />
        </>
      )}
    </div>
  )
}
